import { SIMARouteType } from "../types";
import { Route } from "./route";
import { SurveyPointIndex } from "./survey-point-index";
/**
 * SIMA 線形 (Alignment)。
 * 同一の group_number を共有する複数の Route (IP線, 本線, 中心線形等) と、
 * それに関係付けられた縦断測量成果 (LongitudinalProfile) および横断測量成果 (CrossSectionGroup) を集約します。
 */
export class Alignment {
    routes = [];
    longitudinalProfile;
    crossSections;
    _groupNumber;
    constructor(routes, surveyPointsOrIndex, longitudinalProfile, crossSections) {
        if (routes) {
            const routeList = Array.isArray(routes) ? routes : [routes];
            this.routes = routeList.map((r) => r instanceof Route ? r : new Route(r, surveyPointsOrIndex));
            if (this.routes.length > 0) {
                this._groupNumber = this.routes[0].groupNumber;
            }
        }
        else {
            this.routes = [new Route()];
        }
        if (surveyPointsOrIndex && this.routes.length > 0) {
            this.resolveSurveyPoints(surveyPointsOrIndex);
        }
        this.longitudinalProfile = longitudinalProfile;
        this.crossSections = crossSections;
    }
    /**
     * この線形内の全路線が共有するグループ番号を取得します。
     */
    get groupNumber() {
        return this._groupNumber ?? this.routes[0]?.groupNumber ?? 1;
    }
    set groupNumber(val) {
        this._groupNumber = val;
        for (const route of this.routes) {
            route.groupNumber = val;
        }
    }
    /**
     * 路線を追加します。グループ番号が一致することを保証します。
     */
    addRoute(route, surveyPointsOrIndex) {
        const instance = route instanceof Route ? route : new Route(route, surveyPointsOrIndex);
        if (this._groupNumber !== undefined) {
            instance.groupNumber = this._groupNumber;
        }
        else {
            this._groupNumber = instance.groupNumber;
        }
        this.routes.push(instance);
        return instance;
    }
    /**
     * 本線 (Main Line / routeType 2) を取得します。
     */
    get mainLine() {
        return this.routes.find((r) => r.routeType === SIMARouteType.MAIN_LINE);
    }
    /**
     * 中心線形 (Center Line / routeType 3) を取得します。
     */
    get centerLine() {
        return this.routes.find((r) => r.routeType === SIMARouteType.CENTRAL_LINE);
    }
    /**
     * IP線 (IP Line / routeType 1) を取得します。
     */
    get ipLine() {
        return this.routes.find((r) => r.routeType === SIMARouteType.IP_LINE);
    }
    /**
     * 主要な代表路線を返却します (mainLine があればそれを、なければ最初の路線)。
     */
    get primaryRoute() {
        return this.mainLine ?? this.routes[0] ?? new Route();
    }
    /**
     * 指定された路線番号がこの線形内に含まれているかを返します。
     */
    hasRouteNumber(routeNumber) {
        return this.routes.some((r) => r.routeNumber === routeNumber);
    }
    /**
     * 指定された縦断測量成果がこの線形に一致するかを判定します。
     */
    matchesProfile(profile) {
        return this.hasRouteNumber(profile.routeNumber);
    }
    /**
     * 指定された横断測量成果がこの線形に一致するかを判定します。
     */
    matchesCrossSection(crossSections) {
        return this.hasRouteNumber(crossSections.routeNumber);
    }
    /**
     * 主要路線の路線番号を取得します。
     */
    get routeNumber() {
        return this.primaryRoute.routeNumber;
    }
    /**
     * 主要路線の路線番号を設定し、関連付けられた縦断・横断成果へ同期伝播します。
     */
    set routeNumber(val) {
        this.primaryRoute.routeNumber = val;
        if (this.longitudinalProfile) {
            this.longitudinalProfile.routeNumber = val;
        }
        if (this.crossSections) {
            this.crossSections.routeNumber = val;
        }
    }
    /**
     * 主要路線の路線名を取得します。
     */
    get routeName() {
        return this.primaryRoute.routeName;
    }
    /**
     * 主要路線の路線名を設定し、関連付けられた縦断・横断成果へ同期伝播します。
     */
    set routeName(val) {
        this.primaryRoute.routeName = val;
        if (this.longitudinalProfile) {
            this.longitudinalProfile.routeName = val;
        }
        if (this.crossSections) {
            this.crossSections.routeName = val;
        }
    }
    /**
     * 主要路線の路線種別を取得します。
     */
    get routeType() {
        return this.primaryRoute.routeType;
    }
    set routeType(val) {
        this.primaryRoute.routeType = val;
    }
    // 互換用エイリアス (routeNumber / routeName の単一セッターに完全委譲)
    get alignmentNumber() {
        return this.routeNumber;
    }
    set alignmentNumber(val) {
        this.routeNumber = val;
    }
    get alignmentName() {
        return this.routeName;
    }
    set alignmentName(val) {
        this.routeName = val;
    }
    get alignmentType() {
        return this.routeType;
    }
    set alignmentType(val) {
        this.routeType = val;
    }
    /**
     * 線形内の全路線で参照されている測量座標点一覧を取得します。
     */
    get surveyPoints() {
        const seen = new Set();
        const result = [];
        for (const route of this.routes) {
            for (const pt of route.surveyPoints) {
                if (!seen.has(pt)) {
                    seen.add(pt);
                    result.push(pt);
                }
            }
        }
        return result;
    }
    /**
     * 線形内の全路線に対して SurveyPointIndex または SurveyPoint 配列による座標解決を実行します。
     */
    resolveSurveyPoints(indexOrPoints) {
        const index = Array.isArray(indexOrPoints)
            ? new SurveyPointIndex(indexOrPoints)
            : indexOrPoints;
        for (const route of this.routes) {
            route.resolveSurveyPoints(index);
        }
    }
    /**
     * 主要路線に測量座標点を追加します。
     */
    addSurveyPoint(surveyPoint) {
        this.primaryRoute.addSurveyPoint(surveyPoint);
    }
    /**
     * 縦断測量成果をこの線形に関係付けます。
     */
    linkProfile(profile) {
        this.longitudinalProfile = profile;
    }
    /**
     * 横断測量成果をこの線形に関係付けます。
     */
    linkCrossSections(crossSections) {
        this.crossSections = crossSections;
    }
    /**
     * 構成路線をワイヤフォーマット (ISIMARoute 配列) に変換します。
     */
    toRouteData() {
        return this.routes.map((r) => r.toRouteData());
    }
}
//# sourceMappingURL=alignment.js.map